import { Button } from '@/components/ui/button';
import { LastUsedLoginBadge } from '@/components/LastUsedLoginBadge';

interface GoogleSignInButtonProps {
  /** App-relative path to land on after a successful OAuth round-trip. */
  redirect?: string;
  /** Button label (varies between "Sign in" and "Sign up" contexts). */
  label: string;
  /** Optional marker shown when Google was the browser's last login method. */
  lastUsedLabel?: string;
  className?: string;
}

/**
 * Shared "continue with Google" button. Kicks off the backend OAuth flow,
 * round-tripping through `/login/google/callback`. Used by the login page, the
 * register page, and the login/register modal so the markup and flow stay in
 * one place.
 */
const GoogleSignInButton = ({
  redirect = '/',
  label,
  lastUsedLabel,
  className,
}: GoogleSignInButtonProps) => {
  const handleClick = () => {
    const callbackUrl = `${window.location.origin}/login/google/callback?redirect=${encodeURIComponent(redirect)}`;
    const apiUrl = import.meta.env.VITE_API_URL;
    window.location.href = `${apiUrl}/api/connect/google?redirect=${encodeURIComponent(callbackUrl)}`;
  };

  return (
    <Button
      type="button"
      variant="outline"
      className={
        className
        ?? 'relative w-full border-gray-600 text-white hover:bg-gradient-to-r hover:from-lime-500 hover:to-cyan-500 hover:text-black hover:border-transparent rounded-full flex items-center justify-center gap-2 mt-3'
      }
      onClick={handleClick}
      aria-label={lastUsedLabel ? `${label} ${lastUsedLabel}` : undefined}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      {label}
      {lastUsedLabel && <LastUsedLoginBadge label={lastUsedLabel} />}
    </Button>
  );
};

export default GoogleSignInButton;
